import React, { useState, useMemo } from "react";
import PageLoader from "../components/ui/PageLoader";
import "../assets/css/style.css";
import { FaPlus } from "react-icons/fa";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import Modal from "../components/ui/Modal";
import Button from "../components/ui/Button";
import ContactAlertDetails from "../components/contactAlert/ContactAlertDetails";
import ContactAlertForm from "../components/contactAlert/ContactAlertForm";
import {
  useGetContactAlertsQuery,
  useCreateContactAlertMutation,
  useUpdateContactAlertMutation,
  useDeleteContactAlertMutation,
} from "../apis/contactAlert";
import moment from "moment-timezone";
import Table from "components/ui/Table";

const ContactAlerts = () => {
  const pageSize = 10;
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);

  const { data, isLoading } = useGetContactAlertsQuery();
  const { mutate: createAlert, isLoading: isCreating } =
    useCreateContactAlertMutation();
  const { mutate: updateAlert, isLoading: isUpdating } =
    useUpdateContactAlertMutation();
  const { mutate: deleteAlert, isLoading: isDeleting } =
    useDeleteContactAlertMutation();

  const columns = useMemo(
    () => [
      {
        accessorKey: "index",
        header: "#",
        cell: (_, rowIndex, currentPage) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
        isFilter: false,
        isSortable: false,
      },
      {
        accessorKey: "alertName",
        header: "Alert Name",
        isFilter: true,
      },
      {
        accessorKey: "message",
        header: "Message",
        cell: (row) => (
          <div className="max-w-xs">
            <p className="truncate" title={row.message}>
              {row.message}
            </p>
          </div>
        ),
        isFilter: true,
      },
      {
        accessorKey: "triggerDate",
        header: "Trigger Date",
        cell: (row) => (
          <p>
            {moment(row.triggerDate)
              .tz("Australia/Sydney")
              .format("D MMM YYYY")}
          </p>
        ),
        isFilter: false,
      },
      {
        accessorKey: "expiryDate",
        header: "Expiry Date",
        cell: (row) => (
          <p>
            {row.expiryDate
              ? moment(row.expiryDate)
                  .tz("Australia/Sydney")
                  .format("D MMM YYYY")
              : "No expiry"}
          </p>
        ),
        isFilter: false,
      },
      {
        accessorKey: "recurring",
        header: "Recurring",
        cell: (row) => (
          <span className="capitalize">{row.recurring}</span>
        ),
        isFilter: true,
      },
      {
        accessorKey: "contactCount",
        header: "Contacts",
        cell: (row) => (
          <span className="font-medium">
            {row.contacts?.length || 0}
          </span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "actions",
        header: "Actions",
        cell: (row) => (
          <div className="flex items-center space-x-4">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSelectedAlert(row);
                setIsEditOpen(true);
              }}
              className="edit-icon"
              title="Edit Alert"
            >
              <FiEdit2 fontSize={20} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSelectedAlert(row);
                setIsDeleteOpen(true);
              }}
              className="delete-icon"
              title="Delete Alert"
            >
              <FiTrash2 fontSize={20} />
            </button>
          </div>
        ),
        isFilter: false,
        isSortable: false,
        stopPropagation: true,
        minWidth: "120px",
      },
    ],
    []
  );

  const handleCreateAlert = (formData) => {
    createAlert(formData, {
      onSuccess: () => {
        setIsCreateOpen(false);
      },
    });
  };

  const handleUpdateAlert = (formData) => {
    updateAlert(formData, {
      onSuccess: () => {
        setIsEditOpen(false);
        setSelectedAlert(null);
      },
    });
  };

  const handleDeleteAlert = () => {
    deleteAlert(
      { alertName: selectedAlert.alertName },
      {
        onSuccess: () => {
          setIsDeleteOpen(false);
          setSelectedAlert(null);
        },
      }
    );
  };

  if (isLoading) {
    return <PageLoader />;
  }

  const handleViewRowClick = (row) => {
    setSelectedAlert(row);
    setIsViewOpen(true);
  };

  const CreateAlertButton = () => (
    <Button
      onClick={() => setIsCreateOpen(true)}
      className="black-button flex items-center gap-2"
    >
      <FaPlus fontSize={14} />
      Create Alert
    </Button>
  );

  return (
    <>
      <Modal
        isOpen={isViewOpen}
        onClose={() => {
          setIsViewOpen(false);
          setSelectedAlert(null);
        }}
        title="Alert Details"
        className="max-w-4xl"
      >
        <ContactAlertDetails alertData={selectedAlert} />
      </Modal>

      <Modal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        title="Create New Contact Alert"
        className="max-w-3xl"
      >
        <ContactAlertForm
          onSubmit={handleCreateAlert}
          onCancel={() => setIsCreateOpen(false)}
          isLoading={isCreating}
        />
      </Modal>

      <Modal
        isOpen={isEditOpen}
        onClose={() => {
          setIsEditOpen(false);
          setSelectedAlert(null);
        }}
        title="Edit Contact Alert"
        className="max-w-3xl"
      >
        {selectedAlert && (
          <ContactAlertForm
            alertData={selectedAlert}
            onSubmit={handleUpdateAlert}
            onCancel={() => {
              setIsEditOpen(false);
              setSelectedAlert(null);
            }}
            isLoading={isUpdating}
            isEdit={true}
          />
        )}
      </Modal>

      <Modal
        isOpen={isDeleteOpen}
        onClose={() => {
          setIsDeleteOpen(false);
          setSelectedAlert(null);
        }}
        title="Delete Contact Alert"
        className="max-w-md"
      >
        <div className="text-center">
          <p className="mb-4">
            Are you sure you want to delete the alert "
            {selectedAlert?.alertName}"?
          </p>
          <p className="text-sm text-darkgray mb-6">
            This will remove the alert from{" "}
            {selectedAlert?.contacts?.length || 0} contacts. This action cannot
            be undone.
          </p>
          <div className="flex justify-center gap-3">
            <Button
              className="black-button"
              loading={isDeleting}
              disabled={isDeleting}
              onClick={handleDeleteAlert}
            >
              Yes, Delete
            </Button>
            <Button
              onClick={() => {
                setIsDeleteOpen(false);
                setSelectedAlert(null);
              }}
              className="gray-button"
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      <div className="container mx-auto">
        <Table
          title="Contact Alerts"
          columns={columns}
          data={data || []}
          pageSize={pageSize}
          ButtonCmp={CreateAlertButton}
          onRowClick={handleViewRowClick}
        />
      </div>
    </>
  );
};

export default ContactAlerts;
